import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { subscriptionService } from '../../services/subscriptionService';
import { useAuthStore } from '../../stores/authStore';
import { SubscriptionStatus } from '../../types';

const statusColor = (status: SubscriptionStatus) => {
  switch (status) {
    case SubscriptionStatus.ACTIVE:
    case SubscriptionStatus.ADMIN_GRANT:
      return 'success';
    case SubscriptionStatus.TRIALING:
      return 'info';
    case SubscriptionStatus.PAST_DUE:
      return 'warning';
    case SubscriptionStatus.CANCELED:
    case SubscriptionStatus.UNPAID:
    case SubscriptionStatus.BANNED:
      return 'error';
    default:
      return 'default';
  }
};

export const SubscriptionPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const setSubscription = useAuthStore((state) => state.setSubscription);
  const [searchParams, setSearchParams] = useSearchParams();
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);

  useEffect(() => {
    if (searchParams.get('session_id')) {
      setCheckoutSuccess(true);
      // Clean the URL without reloading
      setSearchParams({}, { replace: true });
      // Refresh subscription data after Stripe redirect
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const { data: sub, isLoading, error } = useQuery({
    queryKey: ['subscription'],
    queryFn: async () => {
      const data = await subscriptionService.getMySubscription();
      setSubscription(data);
      return data;
    },
  });

  const { data: usage } = useQuery({
    queryKey: ['subscription-usage'],
    queryFn: subscriptionService.getUsage,
  });

  const checkoutMutation = useMutation({
    mutationFn: () =>
      subscriptionService.createCheckoutSession(window.location.href),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (err: any) => {
      setMutationError(err?.response?.data?.detail || err?.message || 'Checkout error');
    },
  });

  const portalMutation = useMutation({
    mutationFn: () =>
      subscriptionService.createPortalSession(window.location.href),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (err: any) => {
      setMutationError(err?.response?.data?.detail || err?.message || 'Portal error');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: subscriptionService.cancelSubscription,
    onSuccess: (data) => {
      setSubscription(data);
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !sub) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        {t('subscription.loadError')}
      </Alert>
    );
  }

  const isActive = sub.is_active;
  const isTrialing = sub.status === SubscriptionStatus.TRIALING;
  const isPaidActive = sub.status === SubscriptionStatus.ACTIVE;
  const isPastDue = sub.status === SubscriptionStatus.PAST_DUE;
  const isCanceled =
    sub.status === SubscriptionStatus.CANCELED || sub.status === SubscriptionStatus.UNPAID;
  const isPendingTrial = sub.status === SubscriptionStatus.PENDING_TRIAL;
  const isAdminGrant = sub.status === SubscriptionStatus.ADMIN_GRANT;
  const hasStripeSubscription = !!sub.stripe_subscription_id;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, md: 4 } }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        {t('subscription.title')}
      </Typography>

      {checkoutSuccess && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setCheckoutSuccess(false)}>
          {t('subscription.checkoutSuccess')}
        </Alert>
      )}

      {mutationError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setMutationError(null)}>
          {mutationError}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Current plan card */}
        <Grid item xs={12} md={7}>
          <Card elevation={2} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  {t('subscription.currentPlan')}
                </Typography>
                <Chip
                  label={t(`subscription.status.${sub.status}`)}
                  color={statusColor(sub.status) as any}
                  size="small"
                />
              </Box>

              {/* Trial progress */}
              {isTrialing && sub.trial_days_remaining !== null && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('subscription.trialDaysLeft', { days: sub.trial_days_remaining })}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={((30 - sub.trial_days_remaining) / 30) * 100}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              )}

              {/* Billing period */}
              {isPaidActive && sub.current_period_end && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('subscription.renewsOn', {
                    date: new Date(sub.current_period_end).toLocaleDateString('fr-FR'),
                  })}
                </Typography>
              )}

              {/* Admin grant — free until date */}
              {isAdminGrant && sub.current_period_end && (
                <Alert severity="info" icon={false} sx={{ mb: 2, py: 0.5 }}>
                  {t('subscription.adminGrantUntil', {
                    date: new Date(sub.current_period_end).toLocaleDateString('fr-FR'),
                  })}
                </Alert>
              )}

              {/* Cancel at period end */}
              {sub.cancel_at_period_end && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t('subscription.cancelAtPeriodEnd', {
                    date: sub.current_period_end
                      ? new Date(sub.current_period_end).toLocaleDateString('fr-FR')
                      : '—',
                  })}
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Price display */}
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 3 }}>
                <Typography variant="h3" fontWeight={800}>
                  20€
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  / {t('subscription.month')}
                </Typography>
              </Box>

              {/* Feature list */}
              {[
                'subscription.feature1',
                'subscription.feature2',
                'subscription.feature3',
                'subscription.feature4',
                'subscription.feature5',
              ].map((key) => (
                <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                  <Typography variant="body2">{t(key)}</Typography>
                </Box>
              ))}

              <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {/* Subscribe button */}
                {(isPendingTrial || isTrialing || isCanceled || isPastDue) && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={() => checkoutMutation.mutate()}
                    disabled={checkoutMutation.isPending}
                    startIcon={checkoutMutation.isPending ? <CircularProgress size={16} /> : undefined}
                  >
                    {t('subscription.subscribe')}
                  </Button>
                )}

                {/* Admin grant: setup payment for when free period ends */}
                {isAdminGrant && !hasStripeSubscription && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => checkoutMutation.mutate()}
                    disabled={checkoutMutation.isPending}
                    startIcon={checkoutMutation.isPending ? <CircularProgress size={16} /> : undefined}
                  >
                    {t('subscription.setupPayment')}
                  </Button>
                )}

                {/* Manage billing — only for paid subscriptions with a Stripe customer */}
                {isPaidActive && hasStripeSubscription && (
                  <Button
                    variant="outlined"
                    onClick={() => portalMutation.mutate()}
                    disabled={portalMutation.isPending}
                    startIcon={portalMutation.isPending ? <CircularProgress size={16} /> : <AutorenewIcon />}
                  >
                    {t('subscription.manageBilling')}
                  </Button>
                )}

                {/* Admin grant with payment already set up */}
                {isAdminGrant && hasStripeSubscription && (
                  <Button
                    variant="outlined"
                    onClick={() => portalMutation.mutate()}
                    disabled={portalMutation.isPending}
                    startIcon={portalMutation.isPending ? <CircularProgress size={16} /> : <AutorenewIcon />}
                  >
                    {t('subscription.manageBilling')}
                  </Button>
                )}

                {/* Fix payment — only when there's a real Stripe subscription (not trial expiry) */}
                {isPastDue && sub.stripe_subscription_id && (
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => portalMutation.mutate()}
                    disabled={portalMutation.isPending}
                  >
                    {t('subscription.updatePayment')}
                  </Button>
                )}

                {/* Cancel */}
                {isPaidActive && !sub.cancel_at_period_end && (
                  <Button
                    variant="text"
                    color="error"
                    size="small"
                    startIcon={<CancelIcon />}
                    onClick={() => {
                      if (window.confirm(t('subscription.cancelConfirm'))) {
                        cancelMutation.mutate();
                      }
                    }}
                    disabled={cancelMutation.isPending}
                  >
                    {t('subscription.cancel')}
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Usage card */}
        <Grid item xs={12} md={5}>
          <Card elevation={2} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('subscription.usageThisPeriod')}
              </Typography>
              {usage ? (
                <>
                  <UsageStat label={t('subscription.usageTranscriptions')} value={usage.ai_consultations_count} />
                  <UsageStat label={t('subscription.usageTranscriptionTime')} value={`${usage.transcription_minutes} min`} />
                  <UsageStat label={t('subscription.usageChat')} value={usage.chat_messages_count} />
                  <UsageStat label={t('subscription.usageSuggestions')} value={usage.ai_suggestions_count} />
                  <UsageStat label={t('subscription.usageReports')} value={usage.client_reports_count} />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('subscription.noUsageData')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

const UsageStat = ({ label, value }: { label: string; value: string | number }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value}
    </Typography>
  </Box>
);
